import React, { createContext, useContext, useRef } from 'react';

import { IBaseProps } from '@rbi-ctg/frontend';
import TransferBalanceModal from 'components/transfer-balance-modal';
import {
  IGetEncryptionDetailsMutation,
  IPrepaidsMergeInput,
  UserAccountsDocument,
  useAddAccountMutation,
  useAddCreditAccountMutation,
  useGetEncryptionDetailsMutation
} from 'generated/rbi-graphql';
import useDialogModal from 'hooks/use-dialog-modal';
import useEffectOnce from 'hooks/use-effect-once';
import useErrorModal from 'hooks/use-error-modal';
import { useAuthContext } from 'state/auth';
import { useLocale } from 'state/intl';
import { useMParticleContext } from 'state/mParticle';
import { useNetworkContext } from 'state/network';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import { IAdyenPaymentState, IPaymentPayload, IPaymentState } from 'utils/payment';

import usePayment from './hooks/use-payment';
import {
  IAddPaymentMethodOptions,
  IPaymentDetails,
  IPaymentMethod,
  IReloadPrepaidCard
} from './types';

interface ICurrentBalance {
  currentBalance: number;
}

export interface IPaymentContext {
  loading: boolean;
  paymentMethods: IPaymentMethod[];
  paymentValues: IPaymentDetails;
  clearPaymentValues(): void;
  addPaymentMethod: (
    method: IPaymentPayload,
    options?: IAddPaymentMethodOptions
  ) => Promise<string>;
  deletePaymentMethod: (accountId: string) => Promise<void>;
  getEncryptionDetails: () => Promise<
    Pick<
      IGetEncryptionDetailsMutation['encryptionDetails'],
      'fdPublicKey' | 'fdApiKey' | 'fdAccessToken' | 'fdCustomerId'
    >
  >;
  getPaymentMethods: () => Promise<void>;
  getBalanceFromPaymentMethods: (prepaidPaymentMethod: IPaymentMethod) => number;
  getPrepaidPaymentMethod: () => IPaymentMethod | null;
  getPrepaidCardNumber: () => string | null;
  storePaymentValues: (data: IPaymentDetails) => void;
  reloadPrepaidCard: (input: IReloadPrepaidCard) => Promise<number | undefined>;
  checkoutPaymentMethodId: string;
  setCheckoutPaymentMethodId: (accountId: string) => void;
  defaultPaymentMethodId: string;
  defaultReloadPaymentMethodId: string;
  setDefaultPaymentMethodId: (accountId: string) => void;
  setDefaultReloadPaymentMethodId: (accountId: string) => void;
  prepaidReloadPaymentMethodId: string;
  setPrepaidReloadPaymentMethodId: (accountId: string) => void;
  mergePrepaidCardBalances: (input: IPrepaidsMergeInput) => Promise<ICurrentBalance>;
  setSelected?: (fdAccountId: string) => void;
  openTransferBalanceDialog: () => void;
  getCounter?: () => Promise<void>;
  hasGetPaymentMethodsError: boolean;
  canUseApplePay: boolean;
  canUseGooglePay: boolean;
  paymentProcessor: string | null | undefined;
  isAdyen: boolean;
  transformPaymentValues: (paymentValues: IPaymentState | IAdyenPaymentState) => IPaymentPayload;
}

export const PaymentContext = createContext<IPaymentContext>({} as IPaymentContext);
export const usePaymentContext = () => useContext(PaymentContext);

const clearPersistedPaymentValues = (): void => {
  LocalStorage.setItem(StorageKeys.PAYMENT, {});
};

const persistPaymentValues = (paymentValues: IPaymentDetails): void => {
  LocalStorage.setItem(StorageKeys.PAYMENT, paymentValues);
};

const retrievePersistedPaymentValues = (): IPaymentDetails | null => {
  return LocalStorage.getItem(StorageKeys.PAYMENT);
};

export function PaymentProvider(props: IBaseProps) {
  const { feCountryCode } = useLocale();
  const { updateUserInfo, user } = useAuthContext();
  const [getEncryptionDetailsMutation] = useGetEncryptionDetailsMutation();
  // @TODO Will be deprecated soon to useAddCreditAccountMutation (Reliability Platform Task)
  const [addAccountMutation] = useAddAccountMutation({
    awaitRefetchQueries: true,
    refetchQueries: [{ query: UserAccountsDocument, variables: { feCountryCode } }]
  });
  const [addCreditAccountMutation] = useAddCreditAccountMutation({
    awaitRefetchQueries: true,
    refetchQueries: [{ query: UserAccountsDocument, variables: { feCountryCode } }]
  });
  const mParticle = useMParticleContext();
  const { query } = useNetworkContext();

  const paymentValues = useRef({});

  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Payment Error'
  });

  const [TransferBalanceDialog, openTransferBalanceDialog] = useDialogModal({
    Component: TransferBalanceModal
  });

  const storePaymentValues = (data: IPaymentDetails): void => {
    paymentValues.current = data;
    persistPaymentValues(data);
  };

  const clearPaymentValues = () => {
    paymentValues.current = {};
    clearPersistedPaymentValues();
  };

  useEffectOnce(() => {
    const values = retrievePersistedPaymentValues();
    if (values) {
      paymentValues.current = values;
    }
  });

  const {
    addPaymentMethod,
    deletePaymentMethod,
    getEncryptionDetails,
    getPaymentMethods,
    getBalanceFromPaymentMethods,
    getPrepaidPaymentMethod,
    getPrepaidCardNumber,
    loading,
    paymentMethods,
    defaultPaymentMethodId,
    defaultReloadPaymentMethodId,
    setDefaultPaymentMethodId,
    setDefaultReloadPaymentMethodId,
    checkoutPaymentMethodId,
    setCheckoutPaymentMethodId,
    prepaidReloadPaymentMethodId,
    setPrepaidReloadPaymentMethodId,
    reloadPrepaidCard,
    mergePrepaidCardBalances,
    hasGetPaymentMethodsError,
    canUseApplePay,
    canUseGooglePay,
    paymentProcessor,
    isAdyen,
    transformPaymentValues
  } = usePayment({
    getEncryptionDetailsMutation,
    mParticle,
    openErrorDialog,
    query,
    user,
    updateUserInfo,
    addAccountMutation,
    addCreditAccountMutation
  });

  return (
    <PaymentContext.Provider
      value={{
        loading,
        paymentMethods,
        paymentValues: paymentValues.current,
        clearPaymentValues,
        defaultPaymentMethodId,
        defaultReloadPaymentMethodId,
        setDefaultPaymentMethodId,
        setDefaultReloadPaymentMethodId,
        checkoutPaymentMethodId,
        setCheckoutPaymentMethodId,
        prepaidReloadPaymentMethodId,
        setPrepaidReloadPaymentMethodId,
        storePaymentValues,
        getPaymentMethods,
        getBalanceFromPaymentMethods,
        getPrepaidPaymentMethod,
        getPrepaidCardNumber,
        getEncryptionDetails,
        addPaymentMethod,
        deletePaymentMethod,
        reloadPrepaidCard,
        mergePrepaidCardBalances,
        openTransferBalanceDialog,
        hasGetPaymentMethodsError,
        canUseApplePay,
        canUseGooglePay,
        paymentProcessor,
        isAdyen,
        transformPaymentValues
      }}
    >
      {props.children}
      <TransferBalanceDialog />
      <ErrorDialog />
    </PaymentContext.Provider>
  );
}

export default PaymentContext.Consumer;
